'use client';

import { c as _c } from "react/compiler-runtime";
import { Unplug } from 'lucide-react';
import type { ReactNode } from 'react';
import { Children } from 'react';
import Button from '../common/Button';

// Compact pill for row actions; the default pill padding is sized for page CTAs.
export const ROW_BUTTON = '!px-4 !py-1.5 text-sm';
type SectionProps = {
  title: string;
  description: string;
  /** Right-aligned note beside the heading, e.g. a platform health warning. */
  notice?: ReactNode;
  children: ReactNode;
};
export const IntegrationSection = t0 => {
  const $ = _c(15);
  const {
    title,
    description,
    notice,
    children
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <h3 className="text-sm font-emphasis text-textPrimary">{title}</h3>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== description) {
    t2 = <p className="text-xs text-textSecondary/70">{description}</p>;
    $[2] = description;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div>{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== notice || $[8] !== t3) {
    t4 = <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-1 px-1">{t3}{notice}</div>;
    $[7] = notice;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== children) {
    t5 = <div className="divide-y divide-ds-border-subtle overflow-hidden rounded-xl border border-ds-border-standard bg-background-secondary">{children}</div>;
    $[10] = children;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4 || $[13] !== t5) {
    t6 = <section className="space-y-3">{t4}{t5}</section>;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
};
type RowProps = {
  icon: ReactNode;
  /** Tile colour classes; defaults to the indigo tint. */
  tint?: string;
  name: string;
  status: ReactNode;
  dot?: 'online' | 'offline';
  /** Integration is restricted or failing on our side. */
  limited?: boolean;
  actions?: ReactNode;
  /** Extra content under the row, indented to the text column. */
  children?: ReactNode;
};
export const IntegrationRow = t0 => {
  const $ = _c(31);
  const {
    icon,
    tint: t1,
    name,
    status,
    dot,
    limited,
    actions,
    children
  } = t0;
  const tint = t1 === undefined ? "bg-primary/10 text-primary" : t1;
  let t2;
  if ($[0] !== children) {
    t2 = Children.toArray(children);
    $[0] = children;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const extras = t2;
  const t3 = `flex size-9 shrink-0 items-center justify-center rounded-lg ${tint}`;
  let t4;
  if ($[2] !== icon || $[3] !== t3) {
    t4 = <div className={t3}>{icon}</div>;
    $[2] = icon;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== name) {
    t5 = <p className="min-w-0 truncate text-sm font-emphasis text-textPrimary">{name}</p>;
    $[5] = name;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== dot) {
    t6 = dot && <span className={`size-2 shrink-0 rounded-full ${dot === "online" ? "bg-green-500" : "bg-textSecondary/40"}`} aria-label={dot === "online" ? "Online" : "Offline"} />;
    $[7] = dot;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== limited) {
    t7 = limited && <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-ds-border-standard px-2 py-px text-[11px] font-emphasis text-textSecondary"><span className="size-1.5 rounded-full bg-amber-500" />Limited</span>;
    $[9] = limited;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t5 || $[12] !== t6 || $[13] !== t7) {
    t8 = <div className="flex flex-wrap items-center gap-x-2 gap-y-1">{t5}{t6}{t7}</div>;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== status) {
    t9 = <p className="text-xs text-textSecondary/70">{status}</p>;
    $[15] = status;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t8 || $[18] !== t9) {
    t10 = <div className="min-w-40 flex-1">{t8}{t9}</div>;
    $[17] = t8;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== actions) {
    t11 = actions && <div className="ml-auto flex shrink-0 items-center gap-1">{actions}</div>;
    $[20] = actions;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t10 || $[23] !== t11 || $[24] !== t4) {
    t12 = <div className="flex flex-wrap items-center gap-x-3 gap-y-2">{t4}{t10}{t11}</div>;
    $[22] = t10;
    $[23] = t11;
    $[24] = t4;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== extras) {
    t13 = extras.length > 0 && <div className="mt-3 pl-12">{extras}</div>;
    $[26] = extras;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t12 || $[29] !== t13) {
    t14 = <div className="px-4 py-3.5">{t12}{t13}</div>;
    $[28] = t12;
    $[29] = t13;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  return t14;
};
type DisconnectProps = {
  label: string;
  onClick: () => void;
};

// Icon-only so a list of connected rows doesn't become a column of red pills.
export const DisconnectButton = t0 => {
  const $ = _c(5);
  const {
    label,
    onClick
  } = t0;
  const t1 = `Disconnect ${label}`;
  const t2 = `Disconnect ${label}`;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Unplug size={16} />;
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  let t4;
  if ($[1] !== onClick || $[2] !== t1 || $[3] !== t2) {
    t4 = <Button variant="CLEAR" ariaLabel={t1} tooltip={t2} onClick={onClick} classNames="text-textSecondary hover:text-destructive" icon={t3} />;
    $[1] = onClick;
    $[2] = t1;
    $[3] = t2;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
};